import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, Mic2, Ticket, Users, Loader2 } from "lucide-react";
import { Edicao } from "@/types/edicao";
import { listarEdicoes } from "@/lib/edicoes";
import ListaEsperaTab from "@/components/admin/edicoes/ListaEsperaTab";
import ListaConfirmadosTab from "@/components/admin/edicoes/ListaConfirmadosTab";

type Tab = "espera" | "confirmados";

function formatarData(data: string) {
  if (!data) return "";
  const [ano, mes, dia] = data.split("-");
  const meses = [
    "janeiro","fevereiro","março","abril","maio","junho",
    "julho","agosto","setembro","outubro","novembro","dezembro",
  ];
  return `${parseInt(dia)} de ${meses[parseInt(mes, 10) - 1]} de ${ano}`;
}

export default function AdminEdicaoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState<Tab>("espera");

  useEffect(() => {
    listarEdicoes().then((lista) => {
      const encontrada = lista.find((e) => e.id === id) ?? null;
      setEdicao(encontrada);
      setCarregando(false);
    });
  }, [id]);

  if (carregando) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-black/20" />
      </div>
    );
  }

  if (!edicao) {
    return (
      <div className="p-8 text-center text-black/40 text-sm">
        Edição não encontrada.
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-8 py-5">
        <button
          onClick={() => navigate("/admin/edicoes")}
          className="flex items-center gap-1.5 text-xs font-medium text-black/40 hover:text-black transition mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Voltar para Edições
        </button>

        <div className="flex items-start gap-5">
          {/* Data destaque */}
          <div className="shrink-0 w-16 h-16 bg-black rounded-xl flex flex-col items-center justify-center text-white">
            <span className="text-xs font-medium opacity-60 uppercase tracking-wider leading-none">
              {new Date(edicao.data + "T12:00:00").toLocaleString("pt-BR", { month: "short" })}
            </span>
            <span className="text-2xl font-bold leading-tight">
              {new Date(edicao.data + "T12:00:00").getDate()}
            </span>
          </div>

          <div className="flex-1 space-y-2">
            <div>
              <h1 className="text-xl font-semibold text-black">{edicao.nomeLocal}</h1>
              <p className="text-sm text-black/40">
                {edicao.cidade}/{edicao.uf}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <span className="flex items-center gap-1.5 text-xs text-black/40">
                <CalendarDays className="w-3.5 h-3.5" />
                {formatarData(edicao.data)}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-black/40">
                <Mic2 className="w-3.5 h-3.5" />
                {edicao.atracoes.join(", ")}
              </span>
              {edicao.listaEspera && (
                <span className="flex items-center gap-1.5 text-xs text-black/40">
                  <Users className="w-3.5 h-3.5" />
                  Lista de espera ativa
                  {edicao.limiteListaEspera ? ` · máx. ${edicao.limiteListaEspera}` : ""}
                </span>
              )}
              {edicao.linkIngressos && (
                <a
                  href={edicao.linkIngressos}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-black/40 hover:text-black transition"
                >
                  <Ticket className="w-3.5 h-3.5" />
                  Link de ingressos
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Abas */}
        <div className="flex gap-1 mt-6 border-b border-black/5 -mb-px">
          {(
            [
              { key: "espera", label: "Lista de Espera" },
              { key: "confirmados", label: "Confirmados" },
            ] as { key: Tab; label: string }[]
          ).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setAba(key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                aba === key
                  ? "border-black text-black"
                  : "border-transparent text-black/40 hover:text-black/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Conteúdo das abas */}
      <div className="p-8">
        {aba === "espera" && (
          <ListaEsperaTab
            edicaoId={id!}
            onConfirmado={() => setAba("confirmados")}
          />
        )}
        {aba === "confirmados" && <ListaConfirmadosTab edicaoId={id!} />}
      </div>
    </div>
  );
}
